import {
  faExternalLinkAlt,
  faQrcode,
  faTrash,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { formatClassTime } from "../utils/classTimeUtils";
import { formatDisplayDate } from "../utils/dateFormat";

const LMS_URL = "https://zainematics.learn360lms.com/";

function normalizeStatus(status) {
  return String(status || "").toLowerCase().trim();
}

function statusMeta(status) {
  const s = normalizeStatus(status);
  if (s === "enrolled") {
    return { label: "Enrolled", tone: "enrolled" };
  }
  if (s === "pending") {
    return { label: "Pending", tone: "pending" };
  }
  if (s === "unenrolled") {
    return { label: "Unenrolled", tone: "unenrolled" };
  }
  if (s === "transferred") {
    return { label: "Transferred", tone: "transferred" };
  }
  if (s === "dropped with refund") {
    return { label: "Dropped (refunded)", tone: "dropped" };
  }
  if (s === "dropped without refund") {
    return { label: "Dropped (no refund)", tone: "dropped" };
  }
  if (s === "dropped") {
    return { label: "Dropped", tone: "dropped" };
  }
  if (s.startsWith("dropped")) {
    return { label: status, tone: "dropped" };
  }
  return { label: status || "Other", tone: "other" };
}

function sessionRange(course) {
  const start = course?.Session?.startMonth;
  const end = course?.Session?.endMonth;
  if (start && end) return `${start} – ${end}`;
  if (start || end) return start || end;
  return null;
}

function formatFee(fee) {
  if (fee == null || fee === "") return "—";
  const text = String(fee).trim();
  if (/pkr/i.test(text)) return `${text}/mo`;
  return `PKR ${text}/mo`;
}

export default function EnrolledSubsection({
  course,
  allcourses,
  onDrop,
  onOpenLms,
  onOpenAttendanceQr,
  dropping = false,
  feeRestricted = false,
}) {
  const teacherName =
    course?.Teacher?.name ||
    allcourses?.find((c) => c.id === course.id)?.Teacher?.name ||
    "Teacher TBA";
  const { label, tone } = statusMeta(course.status);
  const canDrop =
    typeof onDrop === "function" && normalizeStatus(course.status) === "pending";
  const isEnrolled = normalizeStatus(course.status) === "enrolled";
  const range = sessionRange(course);
  const year = course?.year ? String(course.year) : null;
  const timing = formatClassTime(course.classTime);

  const handleOpenLms = (event) => {
    if (typeof onOpenLms === "function") {
      event.preventDefault();
      onOpenLms(course);
    }
  };

  return (
    <article className={`mycourses-course-card mycourses-course-card--${tone}`}>
      <div className="mycourses-course-card__accent" aria-hidden="true" />

      <header className="mycourses-course-card__header">
        <div className="mycourses-course-card__heading">
          <div className="mycourses-course-card__title-row">
            <h3 className="mycourses-course-card__title">{course.title}</h3>
            <span className={`mycourses-chip mycourses-chip--${tone}`}>
              {label}
            </span>
          </div>
          <p className="mycourses-course-card__teacher">{teacherName}</p>
        </div>
      </header>

      <div className="mycourses-course-card__tags">
        {range ? (
          <span className="mycourses-soft-chip mycourses-soft-chip--peach">
            {range}
          </span>
        ) : null}
        {year ? (
          <span className="mycourses-soft-chip mycourses-soft-chip--mint">
            {year}
          </span>
        ) : null}
        {course.duration ? (
          <span className="mycourses-soft-chip mycourses-soft-chip--lavender">
            {course.duration}
          </span>
        ) : null}
      </div>

      <div className="mycourses-course-card__facts">
        <div className="mycourses-course-card__fact">
          <span className="mycourses-course-card__fact-label">Starts</span>
          <span className="mycourses-course-card__fact-value">
            {formatDisplayDate(course.startDate, "TBA")}
          </span>
        </div>
        <div className="mycourses-course-card__fact">
          <span className="mycourses-course-card__fact-label">Fee</span>
          <span className="mycourses-course-card__fact-value">
            {formatFee(course.fee)}
          </span>
        </div>
        <div className="mycourses-course-card__fact mycourses-course-card__fact--wide">
          <span className="mycourses-course-card__fact-label">Class times</span>
          <span className="mycourses-course-card__fact-value mycourses-course-card__times">
            {timing}
          </span>
        </div>
      </div>

      {(isEnrolled || canDrop) && (
        <div className="mycourses-course-card__actions">
          {isEnrolled ? (
            <a
              href={LMS_URL}
              target="_blank"
              rel="noopener noreferrer"
              className={`mycourses-course-card__link${
                feeRestricted ? " mycourses-course-card__link--fee-due" : ""
              }`}
              onClick={handleOpenLms}
            >
              Open LMS
              <FontAwesomeIcon icon={faExternalLinkAlt} aria-hidden="true" />
            </a>
          ) : null}
          {isEnrolled &&
          course.attendanceEnabled &&
          typeof onOpenAttendanceQr === "function" ? (
            <button
              type="button"
              className="mycourses-course-card__link"
              onClick={() => onOpenAttendanceQr(course)}
              disabled={!course.attendanceToken}
              // title={
              //   course.attendanceToken
              //     ? "Show attendance QR"
              //     : "Attendance QR is not ready yet — refresh the page"
              // }
            >
              Attendance QR
              <FontAwesomeIcon icon={faQrcode} aria-hidden="true" />
            </button>
          ) : null}
          {canDrop ? (
            <button
              type="button"
              className="mycourses-course-card__drop"
              onClick={() => onDrop(course)}
              disabled={dropping}
              aria-label={`Drop ${course.title}`}
            >
              <FontAwesomeIcon icon={faTrash} aria-hidden="true" />
              {dropping ? "Dropping…" : "Drop course"}
            </button>
          ) : null}
        </div>
      )}
    </article>
  );
}
